import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { getFinanceRecords } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import FilterDropdown from '../../shared/components/FilterDropdown';
import Pagination from '../../shared/components/Pagination';
import usePagination from '../../shared/hooks/usePagination';
import useResizableColumns, { ICON_COL_MIN_WIDTH } from '../../shared/hooks/useResizableColumns';
import '../../shared/styles/resizableTable.css';
import './FinanceRecordsPage.css';

// Leaf-level columns, left to right, matching the table's actual <col> positions.
// Group headers (Token Usage/Lambda/EC2/S3/DynamoDB) span multiple of these via
// colSpan and are not individually resizable — only leaf columns get a drag handle.
const FR_COLUMNS = [
  { key: 'toggle', label: '', width: 40, minWidth: ICON_COL_MIN_WIDTH },
  { key: 'tenant', label: 'Tenant', width: 120 },
  { key: 'division', label: 'Division', width: 120 },
  { key: 'project', label: 'Project', width: 120 },
  { key: 'user', label: 'User', width: 140 },
  { key: 'conversationId', label: 'Conversation ID', width: 160 },
  { key: 'artifact', label: 'Artifact', width: 140 },
  { key: 'timestamp', label: 'Timestamp', width: 150 },
  { key: 'feature', label: 'Feature', width: 120 },
  { key: 'subFeature', label: 'Sub-Feature', width: 120 },
  { key: 'tokenIn', label: 'In', width: 90 },
  { key: 'tokenOut', label: 'Out', width: 90 },
  { key: 'tokenTotal', label: 'Total', width: 90 },
  { key: 'tokenCost', label: 'Token Cost', width: 110 },
  { key: 'lambdaDuration', label: 'Duration', width: 90 },
  { key: 'lambdaCost', label: 'Cost', width: 90 },
  { key: 'ec2Duration', label: 'Duration', width: 90 },
  { key: 'ec2Cost', label: 'Cost', width: 90 },
  { key: 's3Duration', label: 'Duration', width: 90 },
  { key: 's3Cost', label: 'Cost', width: 90 },
  { key: 'dynamoDuration', label: 'Duration', width: 90 },
  { key: 'dynamoCost', label: 'Cost', width: 90 },
  { key: 'totalCost', label: 'Total Cost', width: 120 },
];

const SERVICES = ['Bedrock / AI', 'Lambda', 'EC2', 'S3', 'DynamoDB'];

function fmtCost(n) {
  if (n == null) return '—';
  return '$' + Number(n).toFixed(5);
}

function fmtTokens(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function fmtMs(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString() + ' ms';
}

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-ZA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return ts;
  }
}

// Sum a numeric field across records, return null if all null
function sumField(reqs, field) {
  const vals = reqs.map((r) => r[field]).filter((v) => v != null);
  return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
}

function CopyIcon() {
  return (
    <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function FinanceRecordsPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [toast, setToast] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(FR_COLUMNS);

  const [tenantFilter, setTenantFilter] = useState([]);
  const [userFilter, setUserFilter] = useState([]);
  const [featureFilter, setFeatureFilter] = useState([]);
  const [subFeatureFilter, setSubFeatureFilter] = useState([]);
  const [serviceFilter, setServiceFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const toastTimer = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getFinanceRecords();
      const data = res.data;
      setRecords(Array.isArray(data) ? data : data?.records || []);
      setLastUpdated(new Date());
    } catch {
      setError('Failed to load finance records.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derive filter options from real data
  const tenantOptions = useMemo(
    () => [...new Set(records.map((r) => r.tenantId).filter(Boolean))].sort(),
    [records]
  );
  const userOptions = useMemo(
    () => [...new Set(records.map((r) => r.userId).filter(Boolean))].sort(),
    [records]
  );
  const featureOptions = useMemo(
    () => [...new Set(records.map((r) => r.featureName).filter(Boolean))].sort(),
    [records]
  );
  const subFeatureOptions = useMemo(
    () => [...new Set(records.map((r) => r.subFeature).filter(Boolean))].sort(),
    [records]
  );

  const hasFilters = tenantFilter.length || userFilter.length || featureFilter.length ||
    subFeatureFilter.length || serviceFilter.length || searchQuery.trim();

  const clearFilters = () => {
    setTenantFilter([]); setUserFilter([]); setFeatureFilter([]);
    setSubFeatureFilter([]); setServiceFilter([]); setSearchQuery('');
  };

  // Group flat records by conversationId, compute aggregates for parent row
  const grouped = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const key = r.conversationId;
      if (!map[key]) {
        map[key] = { conversationId: key, requests: [] };
      }
      map[key].requests.push(r);
    });

    return Object.values(map).map(({ conversationId, requests }) => {
      const first = requests[0];
      const multi = requests.length > 1;

      // Aggregate numeric fields across all requests in the conversation
      const parent = {
        conversationId,
        tenantId: first.tenantId,
        division: first.division,
        project: first.project,
        userId: first.userId,
        featureName: first.featureName,
        // Earliest timestamp in the conversation
        timestamp: requests.reduce((min, r) => r.timestamp < min ? r.timestamp : min, first.timestamp),
        // Single-request: show artifact + subFeature; multi: collapse to "—"
        artifactName: multi ? null : first.artifactName,
        subFeature: multi ? null : first.subFeature,
        // Aggregated costs & tokens
        inputTokens: sumField(requests, 'inputTokens'),
        outputTokens: sumField(requests, 'outputTokens'),
        totalTokens: sumField(requests, 'totalTokens'),
        totalTokenCostUsd: sumField(requests, 'totalTokenCostUsd'),
        lambdaRuntimeMs: sumField(requests, 'lambdaRuntimeMs'),
        lambdaCostUsd: sumField(requests, 'lambdaCostUsd'),
        ec2RuntimeMs: sumField(requests, 'ec2RuntimeMs'),
        ec2CostUsd: sumField(requests, 'ec2CostUsd'),
        s3RuntimeMs: sumField(requests, 's3RuntimeMs'),
        s3CostUsd: sumField(requests, 's3CostUsd'),
        dynamoDbRuntimeMs: sumField(requests, 'dynamoDbRuntimeMs'),
        dynamoDbCostUsd: sumField(requests, 'dynamoDbCostUsd'),
        totalCostUsd: sumField(requests, 'totalCostUsd'),
      };

      return { parent, children: multi ? requests : [] };
    });
  }, [records]);

  const filteredGroups = useMemo(() => {
    return grouped.filter((g) => {
      const r = g.parent;
      if (tenantFilter.length && !tenantFilter.includes(r.tenantId)) return false;
      if (userFilter.length && !userFilter.includes(r.userId)) return false;
      if (featureFilter.length && !featureFilter.includes(r.featureName)) return false;
      if (subFeatureFilter.length) {
        // For multi-request conversations, check if any child matches
        const allRows = g.children.length ? g.children : [r];
        if (!allRows.some((row) => subFeatureFilter.includes(row.subFeature))) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const hit =
          (r.conversationId || '').toLowerCase().includes(q) ||
          (r.userId || '').toLowerCase().includes(q) ||
          (r.tenantId || '').toLowerCase().includes(q) ||
          (r.division || '').toLowerCase().includes(q) ||
          (r.project || '').toLowerCase().includes(q) ||
          (r.artifactName || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [grouped, tenantFilter, userFilter, featureFilter, subFeatureFilter, searchQuery]);

  const { currentPage, pageSize, totalPages, paginated, setCurrentPage, setPageSize, resetPage } =
    usePagination(filteredGroups, 10);

  useEffect(() => {
    resetPage();
  }, [tenantFilter, userFilter, featureFilter, subFeatureFilter, serviceFilter, searchQuery, resetPage]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const copyId = (id) => {
    if (navigator.clipboard) navigator.clipboard.writeText(id).catch(() => {});
    setToast(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(false), 1500);
  };

  const handleExportCsv = () => {
    const headers = [
      'Tenant', 'Division', 'Project', 'User', 'Conversation ID', 'Artifact',
      'Timestamp', 'Feature', 'Sub-Feature', 'Input Tokens', 'Output Tokens',
      'Total Tokens', 'Total Token Cost (USD)', 'Lambda Runtime (ms)', 'Lambda Cost (USD)',
      'EC2 Runtime (ms)', 'EC2 Cost (USD)', 'S3 Runtime (ms)', 'S3 Cost (USD)',
      'DynamoDB Runtime (ms)', 'DynamoDB Cost (USD)', 'Total Cost (USD)',
    ];
    const csvRows = [headers.join(',')];
    filteredGroups.forEach(({ children, parent }) => {
      const rows = children.length ? children : [parent];
      rows.forEach((r) => {
        csvRows.push([
          r.tenantId || '', r.division || '', r.project || '', r.userId || '',
          r.conversationId || '', r.artifactName || '', r.timestamp || '',
          r.featureName || '', r.subFeature || '',
          r.inputTokens ?? '', r.outputTokens ?? '', r.totalTokens ?? '',
          r.totalTokenCostUsd ?? '', r.lambdaRuntimeMs ?? '', r.lambdaCostUsd ?? '',
          r.ec2RuntimeMs ?? '', r.ec2CostUsd ?? '', r.s3RuntimeMs ?? '', r.s3CostUsd ?? '',
          r.dynamoDbRuntimeMs ?? '', r.dynamoDbCostUsd ?? '', r.totalCostUsd ?? '',
        ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
      });
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'finance-records.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const renderCells = (r) => (
    <>
      <td>{r.featureName || '—'}</td>
      <td className="fr-td-sub">{r.subFeature || '—'}</td>
      <td className="fr-td-num">{fmtTokens(r.inputTokens)}</td>
      <td className="fr-td-num">{fmtTokens(r.outputTokens)}</td>
      <td className="fr-td-num">{fmtTokens(r.totalTokens)}</td>
      <td className="fr-td-cost">{fmtCost(r.totalTokenCostUsd)}</td>
      <td className="fr-td-num">{fmtMs(r.lambdaRuntimeMs)}</td>
      <td className="fr-td-cost">{fmtCost(r.lambdaCostUsd)}</td>
      <td className="fr-td-num fr-td-dim">{fmtMs(r.ec2RuntimeMs)}</td>
      <td className="fr-td-cost fr-td-dim">{fmtCost(r.ec2CostUsd)}</td>
      <td className="fr-td-num fr-td-dim">{fmtMs(r.s3RuntimeMs)}</td>
      <td className="fr-td-cost fr-td-dim">{fmtCost(r.s3CostUsd)}</td>
      <td className="fr-td-num fr-td-dim">{fmtMs(r.dynamoDbRuntimeMs)}</td>
      <td className="fr-td-cost fr-td-dim">{fmtCost(r.dynamoDbCostUsd)}</td>
      <td className="fr-td-total">{fmtCost(r.totalCostUsd)}</td>
    </>
  );

  return (
    <div className="page-container fr-page">
      {loading && <LoadingSpinner message="Loading finance records..." />}
      {toast && <div className="fr-toast">ID copied to clipboard</div>}

      <div className="fr-header">
        <div>
          <h1 className="page-title fr-title">Finance Records</h1>
          {lastUpdated && (
            <span className="fr-last-updated">Last updated: {lastUpdated.toLocaleTimeString()}</span>
          )}
        </div>
        <div className="fr-header-actions">
          <button className="btn btn-secondary" onClick={fetchAll} disabled={loading}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Refresh
          </button>
          <button className="btn btn-primary" onClick={handleExportCsv} disabled={filteredGroups.length === 0}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Two-row filter bar */}
      <div className="fr-filter-bar">
        <div className="fr-filter-row">
          <div className="fr-search-wrap">
            <svg className="fr-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="fr-search-input"
              placeholder="Search by user, tenant, conversation ID, artifact…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="fr-search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
          <FilterDropdown label="All Tenants" options={tenantOptions} selected={tenantFilter} onChange={setTenantFilter} />
          <FilterDropdown label="All Users" options={userOptions} selected={userFilter} onChange={setUserFilter} />
        </div>
        <div className="fr-filter-row">
          <FilterDropdown label="All Services" options={SERVICES} selected={serviceFilter} onChange={setServiceFilter} />
          <FilterDropdown label="All Features" options={featureOptions.length ? featureOptions : ['QIS', 'Document Generation', 'Execution', 'Project Management']} selected={featureFilter} onChange={setFeatureFilter} />
          <FilterDropdown label="All Sub-Features" options={subFeatureOptions.length ? subFeatureOptions : ['Generate FR', 'Generate Non-FR', 'Traceability Matrix', 'Functional Spec']} selected={subFeatureFilter} onChange={setSubFeatureFilter} />
          {hasFilters ? (
            <button className="fr-clear-btn" onClick={clearFilters}>Clear filters</button>
          ) : null}
        </div>
      </div>

      {/* Table */}
      <div className="card fr-table-card">
        <div className="fr-table-toolbar">
          <span className="fr-table-count">{filteredGroups.length.toLocaleString()} conversations</span>
        </div>
        <div className="fr-table-wrapper">
          <table className="fr-table resizable-table" style={{ width: tableWidth }}>
            <colgroup>
              {FR_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="fr-th-toggle resizable-th" rowSpan={2}>
                  <span className="col-resize-handle" onMouseDown={handleResizeStart('toggle')} />
                </th>
                <th className="resizable-th" rowSpan={2}>Tenant<span className="col-resize-handle" onMouseDown={handleResizeStart('tenant')} /></th>
                <th className="resizable-th" rowSpan={2}>Division<span className="col-resize-handle" onMouseDown={handleResizeStart('division')} /></th>
                <th className="resizable-th" rowSpan={2}>Project<span className="col-resize-handle" onMouseDown={handleResizeStart('project')} /></th>
                <th className="resizable-th" rowSpan={2}>User<span className="col-resize-handle" onMouseDown={handleResizeStart('user')} /></th>
                <th className="resizable-th" rowSpan={2}>Conversation ID<span className="col-resize-handle" onMouseDown={handleResizeStart('conversationId')} /></th>
                <th className="resizable-th" rowSpan={2}>Artifact<span className="col-resize-handle" onMouseDown={handleResizeStart('artifact')} /></th>
                <th className="resizable-th" rowSpan={2}>Timestamp<span className="col-resize-handle" onMouseDown={handleResizeStart('timestamp')} /></th>
                <th className="resizable-th" rowSpan={2}>Feature<span className="col-resize-handle" onMouseDown={handleResizeStart('feature')} /></th>
                <th className="resizable-th" rowSpan={2}>Sub-Feature<span className="col-resize-handle" onMouseDown={handleResizeStart('subFeature')} /></th>
                <th colSpan={3} className="fr-th-group">Token Usage</th>
                <th className="fr-th-num resizable-th" rowSpan={2}>Token Cost<span className="col-resize-handle" onMouseDown={handleResizeStart('tokenCost')} /></th>
                <th colSpan={2} className="fr-th-group">Lambda</th>
                <th colSpan={2} className="fr-th-group">EC2</th>
                <th colSpan={2} className="fr-th-group">S3</th>
                <th colSpan={2} className="fr-th-group">DynamoDB</th>
                <th className="fr-th-total resizable-th" rowSpan={2}>Total Cost<span className="col-resize-handle" onMouseDown={handleResizeStart('totalCost')} /></th>
              </tr>
              <tr>
                <th className="fr-th-num resizable-th">In<span className="col-resize-handle" onMouseDown={handleResizeStart('tokenIn')} /></th>
                <th className="fr-th-num resizable-th">Out<span className="col-resize-handle" onMouseDown={handleResizeStart('tokenOut')} /></th>
                <th className="fr-th-num resizable-th">Total<span className="col-resize-handle" onMouseDown={handleResizeStart('tokenTotal')} /></th>
                <th className="fr-th-num resizable-th">Duration<span className="col-resize-handle" onMouseDown={handleResizeStart('lambdaDuration')} /></th>
                <th className="fr-th-num resizable-th">Cost<span className="col-resize-handle" onMouseDown={handleResizeStart('lambdaCost')} /></th>
                <th className="fr-th-num resizable-th">Duration<span className="col-resize-handle" onMouseDown={handleResizeStart('ec2Duration')} /></th>
                <th className="fr-th-num resizable-th">Cost<span className="col-resize-handle" onMouseDown={handleResizeStart('ec2Cost')} /></th>
                <th className="fr-th-num resizable-th">Duration<span className="col-resize-handle" onMouseDown={handleResizeStart('s3Duration')} /></th>
                <th className="fr-th-num resizable-th">Cost<span className="col-resize-handle" onMouseDown={handleResizeStart('s3Cost')} /></th>
                <th className="fr-th-num resizable-th">Duration<span className="col-resize-handle" onMouseDown={handleResizeStart('dynamoDuration')} /></th>
                <th className="fr-th-num resizable-th">Cost<span className="col-resize-handle" onMouseDown={handleResizeStart('dynamoCost')} /></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && !loading ? (
                <tr>
                  <td colSpan={23} className="fr-empty">
                    {records.length === 0
                      ? 'No finance records available yet — waiting for the API to return records.'
                      : 'No records match the current filters.'}
                  </td>
                </tr>
              ) : (
                paginated.map((group, gi) => {
                  const key = group.parent.conversationId || gi;
                  const isExpanded = expandedGroups[key];
                  const hasChildren = group.children.length > 0;

                  return (
                    <React.Fragment key={key}>
                      {/* Parent / aggregate row */}
                      <tr className={`fr-row ${gi % 2 === 0 ? 'fr-row--lavender' : 'fr-row--white'}`}>
                        <td className="fr-td-toggle">
                          {hasChildren && (
                            <button
                              className={`fr-toggle-btn ${isExpanded ? 'fr-toggle-btn--open' : ''}`}
                              onClick={() => toggleGroup(key)}
                              title={isExpanded ? 'Collapse' : 'Expand'}
                              /* The glyph below is the button's only content, so it
                                 becomes the accessible name — a screen reader would
                                 announce "black right-pointing triangle, button".
                                 title is a mouse-hover tooltip and does not name the
                                 control; aria-label does. */
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          )}
                        </td>
                        <td>{group.parent.tenantId || '—'}</td>
                        <td>{group.parent.division || '—'}</td>
                        <td>{group.parent.project || '—'}</td>
                        <td className="fr-cell-user">{group.parent.userId || '—'}</td>
                        <td className="fr-td-convid">
                          <span className="fr-convid-wrap">
                            <span className="fr-convid-text" title={group.parent.conversationId}>
                              {(group.parent.conversationId || '').slice(0, 13)}{group.parent.conversationId?.length > 13 ? '…' : ''}
                            </span>
                            {group.parent.conversationId && (
                              <button className="fr-copy-btn" onClick={() => copyId(group.parent.conversationId)} title="Copy Conversation ID">
                                <CopyIcon />
                              </button>
                            )}
                          </span>
                        </td>
                        <td className="fr-td-artifact">{group.parent.artifactName || (hasChildren ? '—' : '—')}</td>
                        <td className="fr-td-ts">{fmtTs(group.parent.timestamp)}</td>
                        {renderCells(group.parent)}
                      </tr>

                      {/* Child rows (individual requests in the conversation) */}
                      {hasChildren && isExpanded && group.children.map((child, ci) => (
                        <tr key={`${key}-${ci}`} className="fr-row fr-row--child">
                          <td className="fr-td-toggle"></td>
                          <td className="fr-td-dim">—</td>
                          <td className="fr-td-dim">—</td>
                          <td className="fr-td-dim">—</td>
                          <td className="fr-td-dim">—</td>
                          <td className="fr-td-dim fr-td-convid"><span>—</span></td>
                          <td className="fr-td-artifact">{child.artifactName || '—'}</td>
                          <td className="fr-td-ts">{fmtTs(child.timestamp)}</td>
                          {renderCells(child)}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalItems={filteredGroups.length}
        displayedItems={paginated.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}

export default FinanceRecordsPage;
